import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircle,
    faFilter,
    faListCheck,
    faStop,
} from '@fortawesome/free-solid-svg-icons';
import {
    Button,
    ConfirmModal,
    Dropdown,
    SearchFilterInput,
    alert,
    AlertType,
} from '@quyva/shared-ui';
import axios from 'axios';
import {
    TaskProgressType,
    ProcessType,
    SUSPEND_EVENT_TYPE_USER_INPUT,
    REQUIRED_INPUT_TYPE_FORM,
} from '@quyva/common';
import { getUrl } from '../../utils/getUrl';
import { getFastApiUrl } from '../../utils/getUrl';
import Navbar from '../navbar';
import { CollapsedSideBar } from '../collapsedSideBar';
import { NotificationsColors } from '../../styles/notificationsColors';
import { TasksTablePane } from './TasksTablePane';
import { TaskDetailsPane } from './TaskDetailsPane';
import { Config } from '../../config/envConfig';
import {
    applyWaitingSuspensions,
    getNotificationHistoryForUser,
    getWaitingSuspensionsForUser,
    isExtractionTaskType,
    mapNotificationToTaskRow,
} from '../../utils/notificationsTasksUtils';
import ResumeFormModal from '../agentsUpdates/ResumeFormModal';
import { useAttachmentDownload } from '../../hooks/useAttachmentDownload';

const normalizeTaskKeyPart = (value) => {
    if (value === undefined || value === null) return '';
    return String(value).trim().toLowerCase();
};

const getTaskDurableIdentity = (task) => {
    if (task?.processId) return String(task.processId);
    if (task?.historyId) return String(task.historyId);
    return '';
};

const isTaskProgressLike = (value) => {
    const progressLower = typeof value === 'string' ? value.toLowerCase() : '';
    return ['processing', 'started', 'cancelling', 'paused', 'waiting'].includes(
        progressLower
    );
};

const isTaskCurrentlyProcessing = (task) =>
    isTaskProgressLike(task?.progress) || String(task?.status || '').toLowerCase() === 'processing';

const getTaskSyntheticKeys = (task) => {
    if (Array.isArray(task?.syntheticMergeKeys) && task.syntheticMergeKeys.length > 0) {
        return Array.from(new Set(task.syntheticMergeKeys.map((key) => String(key)).filter(Boolean)));
    }

    const userKey = normalizeTaskKeyPart(task.userId);
    const taskTypeKey = normalizeTaskKeyPart(task.taskType);
    const repoCandidates = [task.repoId, task.repoOrAgentName]
        .map((value) => normalizeTaskKeyPart(value))
        .filter(Boolean);

    return Array.from(
        new Set(repoCandidates.map((repoKey) => `${userKey}-${repoKey}-${taskTypeKey}`))
    );
};

const getTaskSyntheticKey = (task) => getTaskSyntheticKeys(task)[0] || '';

const getPrimaryTaskMergeKey = (task) =>
    (task.mergeKey && String(task.mergeKey)) ||
    (task.id && String(task.id)) ||
    getTaskSyntheticKey(task);

const findExistingTaskMatch = (map, task) => {
    const durableIdentity = getTaskDurableIdentity(task);
    const primaryKey = getPrimaryTaskMergeKey(task);
    const candidateKeys = durableIdentity
        ? [primaryKey].filter(Boolean)
        : [primaryKey, ...getTaskSyntheticKeys(task)].filter(Boolean);

    for (const key of candidateKeys) {
        if (map.has(key)) {
            const existingTask = map.get(key);
            const existingDurableIdentity = getTaskDurableIdentity(existingTask);
            if (durableIdentity && existingDurableIdentity && durableIdentity !== existingDurableIdentity) {
                continue;
            }
            return {
                existingKey: key,
                existingTask,
                candidateKeys,
                primaryKey,
            };
        }
    }

    return {
        existingKey: null,
        existingTask: null,
        candidateKeys,
        primaryKey,
    };
};

const buildLiveDisplayTask = (task) => {
    const baseKey = String(task?.mergeKey || task?.id || 'live-task');
    return {
        ...task,
        id: `live::${baseKey}`,
        mergeKey: `live::${baseKey}`,
        syntheticMergeKeys: [],
        historyId: '',
        isLive: true,
    };
};

const getStrictHistoryTaskKey = (task) => {
    if (task?.historyId) return `history::${String(task.historyId)}`;
    if (task?.id) return `row::${String(task.id)}`;

    return [
        'history-fallback',
        String(task?.userId || ''),
        String(task?.repoId || task?.repoOrAgentName || ''),
        String(task?.taskType || ''),
        String(task?.creationTime || ''),
        String(task?.completionTime || ''),
        String(task?.status || ''),
        String(task?.message || ''),
    ].join('::');
};

const mergeHistoryAndLiveTasks = (historyTasks, liveTasks) => {
    const map = new Map();
    // A history row is stored under `history::<id>`, a key findExistingTaskMatch never
    // generates - it looks rows up by process id and by synthetic repo/type keys. So a live
    // update for a process that ALREADY has a history row was inserted as a SECOND row
    // instead of merging into it. That is exactly an async agent run: its
    // notification_history row is created up front, so history and live coexist from the
    // start and the task showed up twice. Index history rows by process id - the durable
    // identity of a run - so the live row finds its own row.
    const historyKeyByProcessId = new Map();

    (historyTasks || []).forEach((task) => {
        const historyKey = getStrictHistoryTaskKey(task);
        map.set(historyKey, { ...task, isLive: false });
        if (task?.processId) {
            historyKeyByProcessId.set(String(task.processId), historyKey);
        }
    });

    (liveTasks || []).forEach((live) => {
        const matchedHistoryKey = live?.processId
            ? historyKeyByProcessId.get(String(live.processId))
            : null;

        if (matchedHistoryKey && map.has(matchedHistoryKey)) {
            const historyRow = map.get(matchedHistoryKey);
            map.set(matchedHistoryKey, {
                ...historyRow,
                ...live,
                // Same process id means the same run, so the history row's identity is kept:
                // a persisted selectedTaskId must not shift under the user mid-run.
                id: historyRow.id,
                mergeKey: historyRow.mergeKey,
                historyId: historyRow.historyId,
                isLive: true,
                ...(historyRow.creationTime && !live.creationTime
                    ? { creationTime: historyRow.creationTime }
                    : null),
                ...(historyRow.timestamp && (!live.timestamp || live.timestamp === '-')
                    ? { timestamp: historyRow.timestamp }
                    : null),
            });
            return;
        }

        const { existingTask } = findExistingTaskMatch(map, live);
        const existing = existingTask || null;

        if (existing?.historyId && !isTaskCurrentlyProcessing(existing)) {
            const liveDisplayTask = buildLiveDisplayTask(live);
            map.set(liveDisplayTask.mergeKey, liveDisplayTask);
            return;
        }

        const shouldPreserveCreationTime = existing?.creationTime && !live.creationTime;
        const shouldPreserveTimestamp =
            existing?.timestamp && (!live.timestamp || live.timestamp === '-');

        upsertTaskRow(map, live, {
            ...(existing || {}),
            ...live,
            isLive: true,
            ...(shouldPreserveCreationTime ? { creationTime: existing.creationTime } : null),
            ...(shouldPreserveTimestamp ? { timestamp: existing.timestamp } : null),
        });
    });

    return Array.from(map.values());
};

const upsertTaskRow = (map, task, mergedTask) => {
    const { existingKey, candidateKeys, primaryKey } = findExistingTaskMatch(map, task);
    map.set(primaryKey, mergedTask);

    candidateKeys.forEach((key) => {
        if (key !== primaryKey && map.has(key)) {
            map.delete(key);
        }
    });

    if (existingKey && existingKey !== primaryKey && map.has(existingKey)) {
        map.delete(existingKey);
    }
};

const PROCESS_LABEL_MAP = {
    [ProcessType.EXTRACTION]: 'Extraction',
    [ProcessType.FILE_UPLOAD]: 'File Upload',
    [ProcessType.REPO_CREATION]: 'Repo Creation',
    [ProcessType.SupportInfoExtraction]: 'Auto Extraction',
    [ProcessType.KNOWLEDGE_GRAPH_CREATION]: 'Knowledge Graph Creation',
    [ProcessType.QUESTION_GENERATION]: 'Question Generation',
    [ProcessType.ASYNC_AGENT_EXECUTION]: 'Agent Execution',
};

const getProcessLabel = (process) =>
    PROCESS_LABEL_MAP[process] || (process ? String(process) : '-');

const TASKS_STATE_STORAGE_KEY_PREFIX = 'tasksPageState_v1';

const TasksPage = ({
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
}) => {
    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('current')) || null;
        } catch (e) {
            return null;
        }
    }, []);

    const tasksStateStorageKey = useMemo(() => {
        const email = currentUser?.email ? String(currentUser.email) : 'anonymous';
        return `${TASKS_STATE_STORAGE_KEY_PREFIX}:${email}`;
    }, [currentUser?.email]);

    let persistedState = null;
    try {
        const raw = localStorage.getItem(tasksStateStorageKey);
        persistedState = raw ? JSON.parse(raw) : null;
    } catch (e) {
        persistedState = null;
    }

    const hadPersistedSelection = Boolean(
        persistedState && Object.prototype.hasOwnProperty.call(persistedState, 'selectedTaskId')
    );

    const [search, setSearch] = useState(persistedState?.search || '');
    const [statusFilter, setStatusFilter] = useState(
        persistedState?.statusFilter || 'All'
    );
    const [taskTypeFilter, setTaskTypeFilter] = useState(
        persistedState?.taskTypeFilter || 'All'
    );
    const [selectedTaskId, setSelectedTaskId] = useState(
        persistedState?.selectedTaskId || null
    );
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [historyTasks, setHistoryTasks] = useState([]);
    const [hasLoadedTasks, setHasLoadedTasks] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [extractedFilesByTask, setExtractedFilesByTask] = useState({});
    const [taskToCancel, setTaskToCancel] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    // The user's runs parked waiting on them, keyed by process id. Read from
    // suspended_scripts and overlaid on the notification rows by process id.
    const [waitingSuspensions, setWaitingSuspensions] = useState({});
    // The form the user is currently filling in: { formConfig, task }.
    const [resumeForm, setResumeForm] = useState(null);
    const [isResolvingSuspension, setIsResolvingSuspension] = useState(false);
    const [suspensionToCancel, setSuspensionToCancel] = useState(null);

    const leftTableContainerRef = useRef(null);
    const [leftTableContainerWidth, setLeftTableContainerWidth] = useState(0);
    const [leftTableContainerHeight, setLeftTableContainerHeight] = useState(0);
    const [hasUserDraggedTaskSplitter, setHasUserDraggedTaskSplitter] = useState(false);

    const liveTasks = useMemo(() => {
        if (!Array.isArray(messages) || messages.length === 0) return [];

        return messages
            .filter((msg) => isTaskProgressLike(msg.progress ?? msg.status))
            .map((msg) => {
                const normalized = {
                    userMail: msg.userMail || msg.user_mail || '',
                    repoId: msg.repoId,
                    repoName: msg.repoName || msg.repo_name || '',
                    processId: msg.processID || msg.processId || msg.process_id,
                    process: msg.process,
                    progress: msg.progress ?? msg.status,
                    percentageCompletion:
                        typeof msg.percentage_completion === 'number'
                            ? msg.percentage_completion
                            : msg.percentageCompletion,
                    message: msg.message,
                    timeStamp: msg.timeStamp || msg.timestamp || '',
                    creationTime: msg.creationTime || msg.creation_time || '',
                    output_text: msg.output_text,
                    output_files: msg.output_files,
                    templateName: msg.templateName || msg.templatename,
                    addedFileNames:
                        msg.added_file_names || msg.addedFileNames || msg.fileNames,
                    isLive: true,
                };

                normalized.id =
                    normalized.processId ||
                    msg.id ||
                    `${normalizeTaskKeyPart(normalized.userMail)}-${normalizeTaskKeyPart(
                        normalized.repoId || normalized.repoName
                    )}-${normalizeTaskKeyPart(normalized.process)}`;

                normalized.syntheticMergeKeys = Array.from(
                    new Set(
                        [normalized.repoId, normalized.repoName]
                            .map((value) => normalizeTaskKeyPart(value))
                            .filter(Boolean)
                            .map(
                                (repoKey) =>
                                    `${normalizeTaskKeyPart(normalized.userMail)}-${repoKey}-${normalizeTaskKeyPart(
                                        normalized.process
                                    )}`
                            )
                    )
                );

                return mapNotificationToTaskRow(normalized);
            });
    }, [messages]);

    // The suspension overlay is applied last, after history and live rows have been merged
    // into one row per run: a parked run's real status and the input it needs live in
    // suspended_scripts, and must not be overwritten by the notification row's generic
    // 'paused'.
    const tasks = useMemo(
        () =>
            applyWaitingSuspensions(
                mergeHistoryAndLiveTasks(historyTasks, liveTasks),
                waitingSuspensions
            ),
        [historyTasks, liveTasks, waitingSuspensions]
    );

    const loadWaitingSuspensions = async () => {
        const user = JSON.parse(localStorage.getItem('current'));
        setWaitingSuspensions(await getWaitingSuspensionsForUser(user));
    };

    const reloadHistoryTasks = async () => {
        const user = JSON.parse(localStorage.getItem('current'));
        const history = await getNotificationHistoryForUser(user);

        // null means the read failed - keep what is on screen rather than blanking it. An
        // empty array is a real answer and does clear the list.
        if (!history) return;

        // Replace rather than merge: the server is the authority on history rows (one row
        // per run, updated in place). The row's mergeKey is its process id, which is
        // unchanged, so a selected row stays selected across the swap.
        setHistoryTasks(
            history.map((notification) => ({
                ...mapNotificationToTaskRow(notification),
                isLive: false,
            }))
        );

        setHasLoadedTasks(true);
    };

    useEffect(() => {
        reloadHistoryTasks();
        loadWaitingSuspensions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A run parks (or stops being parked) without a terminal notification, so the overlay is
    // re-read whenever a live row reports a suspended state - keyed on the set of suspended
    // process ids so this costs one request per change, not one per notification.
    const suspendedSignatureRef = useRef('');

    useEffect(() => {
        const suspendedIds = liveTasks
            .filter(
                (task) =>
                    ['paused', 'waiting'].includes(
                        String(task.progress || '').toLowerCase()
                    ) && task.processId
            )
            .map((task) => String(task.processId))
            .sort()
            .join(',');

        if (suspendedIds === suspendedSignatureRef.current) return;
        suspendedSignatureRef.current = suspendedIds;
        if (suspendedIds) loadWaitingSuspensions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveTasks]);

    // Persist Tasks page view state across navigation
    useEffect(() => {
        const stateToPersist = {
            search,
            statusFilter,
            taskTypeFilter,
            selectedTaskId,
        };

        try {
            localStorage.setItem(
                tasksStateStorageKey,
                JSON.stringify(stateToPersist)
            );
        } catch (e) {
            // ignore storage errors
        }
    }, [search, statusFilter, taskTypeFilter, selectedTaskId, tasksStateStorageKey]);

    useEffect(() => {
        if (!newNotification) return;

        const effectiveProgress =
            newNotification.progress ?? newNotification.status ?? null;
        if (!effectiveProgress) return;

        const progressLower =
            typeof effectiveProgress === 'string'
                ? effectiveProgress.toLowerCase()
                : '';

        const isTerminal =
            !(effectiveProgress === TaskProgressType.PROCESSING ||
            effectiveProgress === TaskProgressType.STARTED ||
            effectiveProgress === TaskProgressType.CANCELLING) &&
            !['processing', 'started', 'cancelling', 'paused', 'waiting'].includes(
                progressLower
            );

        // For terminal notifications only, reload full history so we capture
        // final outputs and status.
        if (!isTerminal) return;

        reloadHistoryTasks();
        // A terminal notification means nothing is parked any more, so clear the overlay too.
        loadWaitingSuspensions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newNotification]);

    const taskTypesInData = useMemo(
        () =>
            Array.from(
                new Set(tasks.map((t) => t.taskType).filter(Boolean))
            ).sort(),
        [tasks]
    );

    const STATUS_ORDER = [
        'Success',
        'Warning',
        'Fail',
        'Cancelled',
        'Waiting',
        'Paused',
        'Processing',
    ];

    const taskTypeDropdownItems = [
        {
            key: 'All',
            label: 'All task types',
            action: () => setTaskTypeFilter('All'),
            selected: taskTypeFilter === 'All',
        },
        ...taskTypesInData.map((type) => ({
            key: type,
            label: getProcessLabel(type),
            action: () => setTaskTypeFilter(type),
            selected: taskTypeFilter === type,
        })),
    ];

    const getTaskTypeButtonText =
        taskTypeFilter === 'All'
            ? 'All task types'
            : getProcessLabel(taskTypeFilter);

    const statusDropdownItems = [
        {
            key: 'All',
            label: 'All status',
            action: () => setStatusFilter('All'),
            selected: statusFilter === 'All',
        },
        ...STATUS_ORDER.map((status) => ({
            key: status,
            label: status,
            action: () => setStatusFilter(status),
            selected: statusFilter === status,
        })),
    ];

    const getStatusButtonText =
        statusFilter === 'All' ? 'All status' : statusFilter;

    const filteredTasks = useMemo(() => {
        return tasks
            .slice()
            .sort((a, b) => b.timestampMs - a.timestampMs)
            .filter((task) => {
                const searchLower = search.toLowerCase();
                const matchesSearch =
                    !searchLower ||
                    task.repoOrAgentName.toLowerCase().includes(searchLower) ||
                    (task.taskType || '').toLowerCase().includes(searchLower);

                const matchesStatus =
                    statusFilter === 'All' || task.status === statusFilter;

                const matchesTaskType =
                    taskTypeFilter === 'All' || task.taskType === taskTypeFilter;

                return (
                    matchesSearch &&
                    matchesStatus &&
                    matchesTaskType &&
                    true
                );
            });
    }, [tasks, search, statusFilter, taskTypeFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter, taskTypeFilter]);

    const selectedTask = useMemo(() => {
        if (!selectedTaskId) return null;
        return (
            tasks.find((task) => task.mergeKey === selectedTaskId) ||
            tasks.find((task) => task.id === selectedTaskId) ||
            null
        );
    }, [selectedTaskId, tasks]);

    // On very first load (no persisted selection at all),
    // auto-select the most recent task so right panel is populated.
    useEffect(() => {
        if (hadPersistedSelection) return;
        if (selectedTaskId || !tasks.length) return;

        const sorted = [...tasks].sort((a, b) => b.timestampMs - a.timestampMs);
        const first = sorted[0];
        if (first && (first.mergeKey || first.id)) {
            setSelectedTaskId(first.mergeKey || first.id);
        }
    }, [tasks, selectedTaskId, hadPersistedSelection]);

    const selectedTaskFilesKey = selectedTask
        ? selectedTask.mergeKey || selectedTask.id
        : null;

    const selectedTaskFiles =
        selectedTaskFilesKey && extractedFilesByTask[selectedTaskFilesKey]
            ? extractedFilesByTask[selectedTaskFilesKey]
            : [];

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 900);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const element = leftTableContainerRef.current;
        if (!element) return;

        const updateWidth = () => {
            const rect = element.getBoundingClientRect();
            setLeftTableContainerWidth(rect.width);
            setLeftTableContainerHeight(rect.height);
        };

        updateWidth();

        const observer = new ResizeObserver(() => {
            updateWidth();
        });

        observer.observe(element);
        return () => observer.disconnect();
    }, [selectedTaskId]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Success':
                return NotificationsColors.SUCCESS;
            case 'Fail':
                return NotificationsColors.FAIL;
            case 'Warning':
                return NotificationsColors.WARNING;
            case 'Processing':
                return NotificationsColors.ONGOING;
            default:
                return NotificationsColors.ONGOING;
        }
    };

    const handleDownloadRepoFile = async (task, fileName) => {
        if (!task || !fileName) return;

        try {
            const email = task.userId;
            const repoName = task.repoOrAgentName;

            if (!email || !repoName) return;

            const normalizedName = String(fileName).split(/[\\/]/).pop();

            const formData = new FormData();
            formData.append('file_name', normalizedName);
            formData.append('repo_name', repoName);
            formData.append('email', email);

            const response = await axios.post(
                getFastApiUrl('/download-file/'),
                formData,
                {
                    responseType: 'blob',
                }
            );

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = normalizedName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    // An agent's output files are absolute paths outside any repo folder, so
    // handleDownloadRepoFile - which strips to a basename and resolves inside
    // the repo - could never find them. This posts the path to /download-file/.
    const {
        download: handleDownloadOutputFile,
        loadingPath: downloadingOutputPath,
    } = useAttachmentDownload();

    // Runs the backend can still cancel: executing ones. 'cancelling' already has a
    // pending request. Agent runs are never cancellable: once started, their tools act
    // on outside systems, so they can't be stopped part-way cleanly.
    const isTaskProcessing = (task) => {
        if (!task || !task.progress) return false;
        if (task.taskType === ProcessType.ASYNC_AGENT_EXECUTION) return false;
        return [TaskProgressType.PROCESSING, TaskProgressType.STARTED]
            .map((p) => String(p).toLowerCase())
            .includes(String(task.progress).toLowerCase());
    };

    const hasProcessingTasks = useMemo(
        () => tasks.some((task) => isTaskProcessing(task)),
        [tasks]
    );

    const handleRequestCancelTask = (task) => {
        if (!task || !isTaskProcessing(task)) return;
        setTaskToCancel(task);
        setShowCancelConfirm(true);
    };

    const handleConfirmCancelTask = () => {
        if (!socket || !taskToCancel) {
            setShowCancelConfirm(false);
            setTaskToCancel(null);
            return;
        }

        // Cancellation is per run: the backend marks exactly this process id.
        socket.emit('cancelTask', {
            userId: taskToCancel.userId,
            repoId: taskToCancel.repoId,
            repoName: taskToCancel.repoOrAgentName,
            processId: taskToCancel.processId,
            process: taskToCancel.taskType,
        });

        setShowCancelConfirm(false);
        setTaskToCancel(null);
    };

    const handleDownloadExtractedFile = async (task, fileName) => {
        if (!task || !fileName || !isExtractionTaskType(task.taskType)) return;

        try {
            const email = task.userId;
            const folderName = task.repoOrAgentName;
            const templateName = task.templateName;

            if (!email || !folderName || !templateName) return;

            const response = await axios.get(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/download_excel?email=${email}&foldername=${folderName}&templatename=${templateName}&filename=${encodeURIComponent(
                        fileName
                    )}`
                ),
                { responseType: 'blob' }
            );

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading extracted data file:', error);
        }
    };

    // The form a task is waiting on, or null if it is not waiting on one. A run asks its user
    // for data only when the suspension says so ('user_input') and its required_input really
    // describes a form; a wait_for_event pause has neither and is not the user's to release.
    const getWaitingFormConfig = (task) => {
        if (task?.suspendEventType !== SUSPEND_EVENT_TYPE_USER_INPUT) return null;
        const requiredInput = task?.requiredInput;
        if (requiredInput?.type !== REQUIRED_INPUT_TYPE_FORM) return null;
        return requiredInput?.input_json || null;
    };

    const handleOpenResumeForm = (task) => {
        const formConfig = getWaitingFormConfig(task);
        if (!formConfig) return;
        setResumeForm({ formConfig, task });
    };

    // Both outcomes go through the same endpoint: it deletes the suspension either way and
    // decides from should_resume whether the run continues (processing) or ends (cancelled).
    const resolveSuspension = async ({ task, shouldResume, resumeData }) => {
        const processId = task?.processId;
        if (!processId) {
            alert(AlertType.ERROR, 'No process id found for this task.');
            return;
        }

        setIsResolvingSuspension(true);

        // Drop the overlay right away: the suspension is gone server-side the moment this is
        // accepted, so the task must stop offering to act on it.
        setWaitingSuspensions((prev) => {
            const next = { ...(prev || {}) };
            delete next[String(processId)];
            return next;
        });

        try {
            const response = await axios.post(
                getFastApiUrl('/tool_utils/resolve_suspended_script'),
                {
                    // Explicitly a string: the endpoint keys on a String column, and sending a
                    // number is a 422.
                    process_id: String(processId),
                    should_resume: shouldResume,
                    resume_data: resumeData || null,
                },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (!response.data?.success) {
                alert(
                    AlertType.ERROR,
                    shouldResume ? 'Failed to resume the task.' : 'Failed to cancel the task.'
                );
                return;
            }

            alert(
                AlertType.SUCCESS,
                shouldResume
                    ? 'Details submitted. Your task has resumed.'
                    : 'Task cancelled.'
            );
        } catch (e) {
            alert(
                AlertType.ERROR,
                e?.message ||
                    (shouldResume ? 'Failed to resume the task' : 'Failed to cancel the task')
            );
        } finally {
            setIsResolvingSuspension(false);
            // Re-read both sources so the row reflects where the run actually ended up.
            await Promise.all([reloadHistoryTasks(), loadWaitingSuspensions()]);
        }
    };

    const handleResumeFormSubmit = async (answers) => {
        const task = resumeForm?.task;
        setResumeForm(null);
        await resolveSuspension({ task, shouldResume: true, resumeData: answers });
    };

    // The form's Cancel button ends the task. The modal's X only dismisses it, leaving the
    // task Waiting so the form can be reopened - an accidental dismiss must not destroy it.
    const handleResumeFormCancel = () => {
        const task = resumeForm?.task;
        setResumeForm(null);
        if (task) setSuspensionToCancel(task);
    };

    const handleConfirmCancelSuspension = async () => {
        const task = suspensionToCancel;
        setSuspensionToCancel(null);
        if (task) await resolveSuspension({ task, shouldResume: false });
    };

    const renderStatusPill = (status) => {
        const s = String(status || 'Processing');

        const base =
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border';

        const classes = (() => {
            switch (s) {
                case 'Success':
                    return 'text-notifications-success border-notifications-success bg-notifications-success/10';
                case 'Fail':
                    return 'text-notifications-fail border-notifications-fail bg-notifications-fail/10';
                case 'Warning':
                    return 'text-notifications-warning border-notifications-warning bg-notifications-warning/10';
                case 'Cancelled':
                    return 'text-notifications-fail border-notifications-fail bg-notifications-fail/10';
                case 'Waiting':
                case 'Paused':
                    return 'text-landing-orange-600 border-landing-orange-500 bg-landing-orange-500/10';
                case 'Processing':
                default:
                    return 'text-notifications-onGoing border-notifications-onGoing bg-notifications-onGoing/10';
            }
        })();

        return (
            <span className={`${base} ${classes}`}>
                <FontAwesomeIcon icon={faCircle} className='text-[6px]' />
                {s}
            </span>
        );
    };

    const columns = useMemo(
        () => {
            const baseColumns = [
                {
                    title: <span className='whitespace-nowrap'>#</span>,
                    dataIndex: 'serial',
                    key: 'serial',
                    render: (_text, _record, index) => index + 1,
                    width: 80,
                },
                {
                    title: 'TASK',
                    dataIndex: 'taskType',
                    key: 'taskType',
                    render: (value) => getProcessLabel(value),
                    width: 140,
                    ellipsis: true,
                },
                {
                    title: 'REPO / AGENT NAME',
                    dataIndex: 'repoOrAgentName',
                    key: 'repoOrAgentName',
                    width: 200,
                    ellipsis: true,
                },
                {
                    title: 'TIMESTAMP',
                    dataIndex: 'timestamp',
                    key: 'timestamp',
                    sorter: (a, b) => a.timestampMs - b.timestampMs,
                    defaultSortOrder: 'descend',
                    width: 170,
                },
                {
                    title: 'STATUS',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status) => renderStatusPill(status),
                    width: 120,
                },
            ];

            if (hasProcessingTasks) {
                baseColumns.push({
                    title: <span className='whitespace-nowrap'>ACTION</span>,
                    dataIndex: 'actions',
                    key: 'actions',
                    width: 80,
                    align: 'center',
                    fixed: selectedTask ? 'right' : undefined,
                    render: (_value, record) => {
                        if (!isTaskProcessing(record)) {
                            return (
                                <div className='w-full flex items-center justify-center'>
                                    <span className='text-texts-secondary300/40 select-none'>-</span>
                                </div>
                            );
                        }

                        return (
                            <div className='w-full flex items-center justify-center'>
                                <Button
                                    className='w-[24px] h-[24px] flex items-center justify-center'
                                    type='danger'
                                    hoverText={`Stop ${getProcessLabel(
                                        record.taskType
                                    )} on ${record.repoOrAgentName}`}
                                    icon={faStop}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRequestCancelTask(record);
                                    }}
                                />
                            </div>
                        );
                    },
                });
            }

            return baseColumns;
        },
        [hasProcessingTasks, selectedTask]
    );

    return (
        <div className='z-0 w-full h-[99vh] pt-2'>
            <ReflexContainer orientation='vertical'>
                {!isSmallScreen && (
                    <ReflexElement
                        className='left-pane mr-0.5'
                        minSize={50}
                        size={50}
                        maxSize={50}
                    >
                        <div className='pane-content w-full h-full bg-transparent'>
                            <CollapsedSideBar
                                icon={faListCheck}
                                iconColor={'icons-primary'}
                                title={'Tasks'}
                                setIsRepoViewExpanded={() => {}}
                            />
                        </div>
                    </ReflexElement>
                )}

                <ReflexSplitter
                    style={{
                        border: 0,
                        background: 'white',
                        width: '6px',
                        zIndex: 10,
                        height: '98vh',
                    }}
                />

                <ReflexElement className='right-pane ml-0.5'>
                    <div className='pane-content w-full h-full bg-transparent'>
                        <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl flex flex-col'>
                            <div className='w-full h-14 px-2 pt-2'>
                                <Navbar
                                    newNotification={newNotification}
                                    setNewNotification={setNewNotification}
                                    viewNotifications={viewNotifications}
                                    setViewNotifications={setViewNotifications}
                                    messages={messages}
                                    socket={socket}
                                />
                            </div>

                            <div className='flex-1 w-full h-full pt-6 pl-2 pr-0 pb-4'>
                                <div className='w-full h-full bg-backgrounds-white rounded-xl border border-backgrounds-border flex flex-col'>
                                    <div className='px-4 py-3 border-b border-backgrounds-border flex flex-wrap items-center gap-4 bg-backgrounds-primary100/60'>
                                        <div
                                            className='flex items-center text-texts-secondary300'
                                            title='Filter and search tasks'
                                        >
                                            <FontAwesomeIcon
                                                icon={faFilter}
                                                className='text-sm text-texts-secondary300'
                                            />
                                        </div>
                                        <div className='flex-1 flex items-center gap-3 min-w-[260px]'>
                                            <SearchFilterInput
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                placeholder='Search by repo/agent or task'
                                                className='flex-1 min-w-[380px]'
                                            />
                                            <div className='flex items-center gap-2 ml-auto text-xs'>
                                                <Dropdown
                                                    dropDownList={taskTypeDropdownItems}
                                                    showButtonText
                                                    buttonText={getTaskTypeButtonText}
                                                    buttonClassName='h-8 w-[11rem] px-2 text-xs border border-backgrounds-border bg-backgrounds-white text-texts-secondary300'
                                                    listClassName='max-h-40 overflow-y-auto min-w-[11rem]'
                                                />
                                                <Dropdown
                                                    dropDownList={statusDropdownItems}
                                                    showButtonText
                                                    buttonText={getStatusButtonText}
                                                    buttonClassName='h-8 w-[8rem] px-2 text-xs border border-backgrounds-border bg-backgrounds-white text-texts-secondary300'
                                                    listClassName='max-h-40 overflow-y-auto min-w-[8rem]'
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className='flex-1 overflow-hidden'>
                                        {!selectedTask ? (
                                            <TasksTablePane
                                                filteredTasks={filteredTasks}
                                                columns={columns}
                                                selectedTaskId={selectedTaskId}
                                                setSelectedTaskId={setSelectedTaskId}
                                                hasUserDraggedTaskSplitter={hasUserDraggedTaskSplitter}
                                                leftTableContainerRef={leftTableContainerRef}
                                                leftTableContainerWidth={leftTableContainerWidth}
                                                leftTableContainerHeight={leftTableContainerHeight}
                                                setHasUserDraggedTaskSplitter={
                                                    setHasUserDraggedTaskSplitter
                                                }
                                                selectedTask={selectedTask}
                                                currentPage={currentPage}
                                                setCurrentPage={setCurrentPage}
                                            />
                                        ) : (
                                            <ReflexContainer orientation='vertical' className='w-full h-full'>
                                                <ReflexElement
                                                    className='bg-backgrounds-white overflow-hidden rounded-l-xl rounded-r-none'
                                                    minSize={520}
                                                    flex={0.55}
                                                >
                                                    <TasksTablePane
                                                        filteredTasks={filteredTasks}
                                                        columns={columns}
                                                        selectedTaskId={selectedTaskId}
                                                        setSelectedTaskId={setSelectedTaskId}
                                                        hasUserDraggedTaskSplitter={
                                                            hasUserDraggedTaskSplitter
                                                        }
                                                        leftTableContainerRef={leftTableContainerRef}
                                                        leftTableContainerWidth={leftTableContainerWidth}
                                                        leftTableContainerHeight={leftTableContainerHeight}
                                                        setHasUserDraggedTaskSplitter={
                                                            setHasUserDraggedTaskSplitter
                                                        }
                                                        selectedTask={selectedTask}
                                                        currentPage={currentPage}
                                                        setCurrentPage={setCurrentPage}
                                                    />
                                                </ReflexElement>

                                                <ReflexSplitter
                                                    style={{
                                                        border: 0,
                                                        background: 'rgba(0, 0, 0, 0.06)',
                                                        width: '2px',
                                                        zIndex: 10,
                                                    }}
                                                    onStopResize={() => {
                                                        setHasUserDraggedTaskSplitter(true);
                                                    }}
                                                />

                                                <ReflexElement
                                                    className='bg-backgrounds-white overflow-hidden rounded-r-xl rounded-l-none'
                                                    minSize={320}
                                                    flex={0.45}
                                                >
                                                    <TaskDetailsPane
                                                        selectedTask={selectedTask}
                                                        selectedTaskFiles={selectedTaskFiles}
                                                        setSelectedTaskId={setSelectedTaskId}
                                                        renderStatusPill={renderStatusPill}
                                                        waitingFormConfig={getWaitingFormConfig(
                                                            selectedTask
                                                        )}
                                                        onFillWaitingForm={() =>
                                                            handleOpenResumeForm(selectedTask)
                                                        }
                                                        onCancelWaitingTask={() =>
                                                            setSuspensionToCancel(selectedTask)
                                                        }
                                                        isResolvingSuspension={
                                                            isResolvingSuspension
                                                        }
                                                        isExtractionTaskType={isExtractionTaskType}
                                                        extractedFilesByTask={extractedFilesByTask}
                                                        setExtractedFilesByTask={setExtractedFilesByTask}
                                                        handleDownloadExtractedFile={
                                                            handleDownloadExtractedFile
                                                        }
                                                        handleDownloadRepoFile={
                                                            handleDownloadRepoFile
                                                        }
                                                        handleDownloadOutputFile={
                                                            handleDownloadOutputFile
                                                        }
                                                        downloadingOutputPath={
                                                            downloadingOutputPath
                                                        }
                                                    />
                                                </ReflexElement>
                                            </ReflexContainer>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ReflexElement>
            </ReflexContainer>

            {showCancelConfirm && taskToCancel && (
                <ConfirmModal
                    modalText={`Are you sure you want to cancel ${getProcessLabel(
                        taskToCancel.taskType
                    )} on ${taskToCancel.repoOrAgentName}?`}
                    onConfirm={handleConfirmCancelTask}
                    onCancel={() => {
                        setShowCancelConfirm(false);
                        setTaskToCancel(null);
                    }}
                />
            )}

            {resumeForm && (
                <ResumeFormModal
                    formConfig={resumeForm.formConfig}
                    taskLabel={resumeForm.task?.repoOrAgentName || ''}
                    onSubmit={handleResumeFormSubmit}
                    onCancel={handleResumeFormCancel}
                    onClose={() => setResumeForm(null)}
                />
            )}

            {suspensionToCancel && (
                <ConfirmModal
                    modalText={`Cancel the task on ${suspensionToCancel.repoOrAgentName}? It is waiting for details, and cancelling ends it without completing.`}
                    onConfirm={handleConfirmCancelSuspension}
                    onCancel={() => setSuspensionToCancel(null)}
                />
            )}
        </div>
    );
};

export default TasksPage;
